import AppDateInput from '@/app/admin/components/AppDateInput'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/24/outline'

import AmicalAdherentForm from '../../admin/modules/GestionHotel/MoteurReservation/components/AmicalReservationModal/AmicalAdherentForm'
import useAmicalForm from '../../admin/modules/GestionHotel/MoteurReservation/components/AmicalReservationModal/useAmicalForm'
import {
  Button,
  CARD_TYPE,
  CardTypeBadge,
  Field,
  NumberStepper,
  formatPeriode,
  formatTND,
  hasDailyStock,
  inputClass,
  labelClass,
  StockAccessInfo,
  clampCarteBookableDate,
  getMinBookableDateForCarte,
  toDateInputValue,
  validateCarteReservationDate,
} from '../../admin/modules/GestionCartesAccesEtBienEtre/shared'
import CarteDetailsStep from '../../admin/modules/GestionCartesAccesEtBienEtre/Recherche/CarteDetailsStep'
import StepIndicator from '../../admin/modules/GestionCartesAccesEtBienEtre/Recherche/StepIndicator'
import useAmicale from '../hooks/useAmicale'
import AmicalCaReservationSuccess from './AmicalCaReservationSuccess'
import buildAmicalCaReservationPayload from './buildAmicalCaReservationPayload'

const getAccesPaxBounds = (carte) => {
  const maxFromAcces = (Number(carte?.max_adults) || 0) + (Number(carte?.max_children) || 0)
  const max = maxFromAcces > 0 ? maxFromAcces : Number(carte?.max_pax) || 99
  return { min: 1, max: Math.max(1, max) }
}

const AmicalCaReservationModal = ({ carte, open, defaults, onClose, onConfirm }) => {
  const { t } = useTranslation()
  const { client: amicalClient } = useAmicale()
  const form = useAmicalForm()
  const [phase, setPhase] = useState('form')
  const [step, setStep] = useState(1)
  const [pax, setPax] = useState(1)
  const [date, setDate] = useState('')
  const [clientName, setClientName] = useState('')
  const [clientNameError, setClientNameError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [createdRef, setCreatedRef] = useState(null)
  const [quoteParams, setQuoteParams] = useState(null)

  const isAcces = carte?.type === CARD_TYPE.ACCES
  const dailyStock = hasDailyStock(carte)
  const perPax = carte?.pricing_type === 'per_pax'
  const needsReservationStep = isAcces && (dailyStock || perPax)

  const steps = useMemo(() => {
    const base = [
      { id: 1, label: t('amicales.reservationModal.stepDetails') },
      { id: 2, label: t('amicales.reservationModal.stepClient') },
    ]
    if (needsReservationStep) {
      return [...base, { id: 3, label: t('amicales.reservationModal.stepReservation') }]
    }
    return base
  }, [needsReservationStep, t])

  const isDetailsStep = step === 1
  const isClientStep = step === 2
  const isReservationStep = needsReservationStep && step === 3

  useEffect(() => {
    if (!open) {
      form.reset()
      setPhase('form')
      setStep(1)
      setError('')
      setClientName('')
      setClientNameError('')
      setCreatedRef(null)
      setQuoteParams(null)
      return
    }
    if (!carte) return
    const accesBounds = getAccesPaxBounds(carte)
    const defaultPax = isAcces
      ? Math.min(Math.max(Number(defaults?.pax) || 1, accesBounds.min), accesBounds.max)
      : Math.max(carte.min_pax, Number(defaults?.pax) || carte.min_pax)
    setPax(defaultPax)
    setDate(
      isAcces
        ? clampCarteBookableDate(carte, defaults?.date || carte.start_date)
        : defaults?.date || carte.start_date || '',
    )
    setStep(1)
    setPhase('form')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, carte?.id, defaults?.date, defaults?.pax, isAcces])

  const total = useMemo(() => {
    if (!carte) return 0
    if (perPax) return carte.price * pax
    return carte.price
  }, [carte, perPax, pax])

  const paxBounds = useMemo(() => {
    if (!carte) return { min: 1, max: 1 }
    if (isAcces) return getAccesPaxBounds(carte)
    return { min: carte.min_pax, max: carte.max_pax }
  }, [carte, isAcces])

  if (!open || !carte) return null

  const validateClientName = () => {
    const name = clientName.trim()
    if (!name) {
      setClientNameError(t('amicales.reservationModal.clientNameRequired'))
      return false
    }
    setClientNameError('')
    return true
  }

  const handleNext = () => {
    setError('')
    if (isDetailsStep) {
      setStep(2)
      return
    }
    if (!validateClientName()) return
    if (!form.validateAdherent()) return
    if (needsReservationStep) {
      setStep(3)
      return
    }
    handleSubmit()
  }

  const handleBack = () => {
    setError('')
    setClientNameError('')
    setStep((s) => Math.max(s - 1, 1))
  }

  const handleSubmit = async () => {
    setError('')
    if (!validateClientName()) return
    if (needsReservationStep && dailyStock) {
      const dateError = validateCarteReservationDate(carte, date)
      if (dateError) {
        setError(dateError)
        return
      }
    }
    if (!form.validateAdherent()) return

    const formValues = { ...form.values, client_name: clientName.trim() }
    const params = {
      carte,
      client: amicalClient,
      amicalForm: form.values,
      clientName: clientName.trim(),
      voucherEmail: amicalClient?.voucherEmail,
      date,
      pax,
      totalPrice: total,
      currency: 'TND',
    }

    setSubmitting(true)
    try {
      const payload = buildAmicalCaReservationPayload(carte, formValues, {
        date,
        pax,
        total,
        isAcces,
        amicalClient,
      })
      const data = await onConfirm(payload)
      const ref = data?.ref || data?.reservation?.reference || data?.reservation?.ref
      setCreatedRef(ref || null)
      setQuoteParams(params)
      setPhase('done')
    } catch {
      setError(t('amicales.reservationModal.createFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleSuccessClose = () => {
    onClose?.()
  }

  const showDateField = !isAcces || dailyStock
  const showPaxField = !isAcces || perPax

  return (
    <div className="fixed inset-0 z-overlay flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={phase === 'done' || submitting ? undefined : onClose}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="amical-ca-modal-title"
        className="relative z-10 flex max-h-[min(92vh,860px)] w-full max-w-[1120px] flex-col overflow-hidden rounded-none bg-white shadow-2xl sm:rounded-2xl"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-6">
          <h2 id="amical-ca-modal-title" className="text-lg font-bold text-gray-900">
            {isAcces
              ? t('amicales.reservationModal.titleAccess')
              : t('amicales.reservationModal.titleWellness')}
          </h2>
          {phase !== 'done' && !submitting && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>

        {phase === 'done' ? (
          <AmicalCaReservationSuccess
            reservationRef={createdRef}
            quoteParams={quoteParams}
            onClose={handleSuccessClose}
          />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <StepIndicator steps={steps} current={step} />

              {isDetailsStep ? (
                <CarteDetailsStep carte={carte} />
              ) : (
                <>
                  <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{carte.label}</h3>
                        <p className="text-sm text-gray-500">{carte.facility_label}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <CardTypeBadge type={carte.type} />
                          <span className="text-xs text-gray-500">
                            {formatPeriode(carte.start_date, carte.end_date)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-brand-700">{formatTND(total)}</p>
                        {perPax && (
                          <p className="text-xs text-gray-400">{t('amicales.reservationModal.perPerson')}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3">
                      <StockAccessInfo carte={carte} appearance="banner" />
                    </div>
                  </div>

                  {isClientStep ? (
                    <div className="space-y-4">
                      <div className="glass-card p-4 sm:p-6">
                        <label className={`${labelClass} mb-1.5 block`}>
                          {t('amicales.reservationModal.clientNameLabel')}{' '}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            className={`${inputClass} !pl-10`}
                            value={clientName}
                            onChange={(e) => {
                              setClientName(e.target.value)
                              setClientNameError('')
                            }}
                            placeholder={t('amicales.reservationModal.clientNamePlaceholder')}
                            aria-label={t('amicales.reservationModal.clientNameAria')}
                          />
                        </div>
                        {clientNameError ? (
                          <p className="mt-1 text-xs font-medium text-red-600">{clientNameError}</p>
                        ) : null}
                      </div>

                      <AmicalAdherentForm
                        values={form.values}
                        errors={form.errors}
                        onChange={form.setField}
                        voucherEmail={amicalClient?.voucherEmail}
                      />
                    </div>
                  ) : isReservationStep ? (
                    <div className="space-y-4">
                      {showDateField && (
                        <Field
                          label={t('amicales.reservationModal.dateLabel')}
                          required
                          error={error || undefined}
                        >
                          <AppDateInput
                            value={date}
                            min={isAcces ? getMinBookableDateForCarte(carte) : toDateInputValue(carte.start_date)}
                            max={toDateInputValue(carte.end_date)}
                            required
                            onChange={(value) => {
                              setDate(value)
                              setError('')
                            }}
                            compact
                          />
                        </Field>
                      )}
                      {showPaxField && (
                        <Field
                          label={t('amicales.reservationModal.personsLabel', {
                            min: paxBounds.min,
                            max: paxBounds.max,
                          })}
                        >
                          <NumberStepper
                            value={pax}
                            onChange={setPax}
                            min={paxBounds.min}
                            max={paxBounds.max}
                            ariaLabel={t('amicales.reservationModal.personsAria')}
                          />
                        </Field>
                      )}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3 sm:px-6">
              {isDetailsStep ? (
                <>
                  <Button variant="ghost" onClick={onClose} disabled={submitting}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="cta" onClick={handleNext} disabled={submitting}>
                    {t('amicales.reservationModal.next')}
                  </Button>
                </>
              ) : isClientStep ? (
                <>
                  <Button variant="ghost" onClick={handleBack} disabled={submitting}>
                    {t('amicales.reservationModal.back')}
                  </Button>
                  <Button variant="cta" onClick={handleNext} disabled={submitting}>
                    {needsReservationStep
                      ? t('amicales.reservationModal.next')
                      : t('amicales.reservationModal.createRequest')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={handleBack} disabled={submitting}>
                    {t('amicales.reservationModal.back')}
                  </Button>
                  <Button variant="cta" onClick={handleSubmit} loading={submitting}>
                    {t('amicales.reservationModal.createRequest')}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AmicalCaReservationModal
